import React, { useCallback, useEffect, useEffectEvent, useMemo } from "react";
import classNames from "classnames";
import { FormikProvider, useFormik } from "formik";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { isEqual } from "@common/helpers/comparison";
import { RuleGroupType } from "@common/types/transactionRule";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useEntities } from "@hooks/useEntities";
import { useNormalizedDescriptions } from "@hooks/useNormalizedDescriptions";
import { Switch } from "@design-system/Switch/Switch";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Menu } from "@design-system/Menu/Menu.lib";
import { AlertIcon, CloseIcon, DragHandle6DotIcon, PlusIcon } from "@icons";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { RuleConditionForm } from "../../../settings/Rules/TransactionRuleForm/RuleConditionsForm/RuleConditionForm";
import { getSupportedRuleConditionTypes } from "../../../settings/Rules/TransactionRuleForm/RuleConditionsForm/supportedRuleConditionTypes";
import { RuleActionForm } from "../../../settings/Rules/TransactionRuleForm/RuleActionsForm/RuleActionForm";
import {
    FormRuleConditionType,
    TransactionRuleConditionFormSchema,
    TransactionRuleFormSchema,
} from "../../../settings/Rules/TransactionRuleForm/constants";
import { useActionsContext } from "./ActionsContext";
import { useActionRowBucketCount } from "./useActionRowBucketCount";
import { useDeleteActionRow } from "./useDeleteActionRow";
import { ActionRowDefinition } from "./types";
import {
    buildConditionsAfterAddingCondition,
    shouldEnableCreateRuleAfterAddingCondition,
} from "./seedActionRow";
import {
    CREATE_RULE_REQUIRES_CONDITION_TOOLTIP,
    isCreateRuleMissingConditions,
} from "./createRuleValidation";
import styles from "./ActionsCard.module.scss";

const DRAWER_CONTROL_BUTTON_SIZE = "md" satisfies TriggerButtonProps["size"];

interface Props {
    row: ActionRowDefinition;
}

export const ActionRow: React.FC<Props> = ({ row }) => {
    const {
        rows,
        updateRow,
        ruleGroupId,
        defaultConditions,
        filters,
        similarToFallbackLabel,
        isRuleAuthoringAvailable,
    } = useActionsContext();
    const entities = useEntities();

    const canReorderOrDelete = rows.length > 1;

    const {
        attributes: sortableAttributes,
        listeners: sortableListeners,
        setNodeRef: setSortableRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: row.id });

    const rowStyle: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    /**
     * Wrap the row in its own Formik so we can reuse the existing
     * `RuleConditionForm` / `RuleActionForm` pieces which rely on Formik's
     * `useField("conditions.N.X")` / `useField("actions.0.X")` paths.
     * Values are synced up to the parent context on change via
     * `useEffect` below.
     */
    const formik = useFormik<TransactionRuleFormSchema>({
        initialValues: {
            groupId: ruleGroupId,
            conditions: row.conditions,
            actions: [row.action],
            note: "",
            applyToExistingTransactions: false,
            excludedTransactionsIds: [],
            includedTransactionsIds: [],
        },
        onSubmit: () => {},
        enableReinitialize: false,
    });

    const syncGroupIdIntoForm = useEffectEvent((groupId: string) => {
        formik.setFieldValue("groupId", groupId);
    });
    useEffect(() => {
        syncGroupIdIntoForm(ruleGroupId);
    }, [ruleGroupId]);

    const syncConditionsIntoForm = useEffectEvent(
        (conditions: ActionRowDefinition["conditions"]) => {
            if (!isEqual(formik.values.conditions, conditions)) {
                formik.setFieldValue("conditions", conditions);
            }
        },
    );
    useEffect(() => {
        syncConditionsIntoForm(row.conditions);
    }, [row.conditions]);

    const syncActionIntoForm = useEffectEvent(
        (action: ActionRowDefinition["action"]) => {
            if (!isEqual(formik.values.actions[0], action)) {
                formik.setFieldValue("actions.0", action);
            }
        },
    );
    useEffect(() => {
        syncActionIntoForm(row.action);
    }, [row.action]);

    const formConditions = formik.values.conditions;
    const syncConditionsIntoRow = useEffectEvent(
        (conditions: typeof formConditions) => {
            if (!isEqual(conditions, row.conditions)) {
                updateRow(row.id, { conditions });
            }
        },
    );
    useEffect(() => {
        syncConditionsIntoRow(formConditions);
    }, [formConditions]);

    const formAction = formik.values.actions[0];
    const syncActionIntoRow = useEffectEvent((action: typeof formAction) => {
        if (!isEqual(action, row.action)) {
            updateRow(row.id, { action });
        }
    });
    useEffect(() => {
        syncActionIntoRow(formAction);
    }, [formAction]);

    const handleToggleCreateRule = useCallback(
        (next: boolean) => {
            updateRow(row.id, { createRule: next });
        },
        [row.id, updateRow],
    );

    const handleAddCondition = useCallback(
        (conditionType: FormRuleConditionType) => {
            const shouldEnableCreateRule =
                shouldEnableCreateRuleAfterAddingCondition(
                    formik.values.conditions,
                );
            const result = buildConditionsAfterAddingCondition({
                currentConditions: formik.values.conditions,
                defaultConditions,
                conditionType,
            });
            formik.setFieldValue("conditions", result.conditions);
            updateRow(row.id, {
                conditions: result.conditions,
                defaultConditionIds:
                    result.defaultConditionIds.length > 0
                        ? result.defaultConditionIds
                        : undefined,
                createRule: shouldEnableCreateRule ? true : undefined,
            });
        },
        [defaultConditions, formik, row.id, updateRow],
    );

    const handleRemoveCondition = useCallback(
        (id: string) => {
            const nextConditions = formik.values.conditions.filter(
                (c) => c.id !== id,
            );
            formik.setFieldValue("conditions", nextConditions);
        },
        [formik],
    );

    const selectedConditionTypes = useMemo(
        () =>
            formik.values.conditions
                .map((c) => c.conditionType)
                .filter(
                    (v): v is FormRuleConditionType =>
                        v !== undefined &&
                        v !== FormRuleConditionType.BANK_DESCRIPTION &&
                        v !== FormRuleConditionType.AMOUNT,
                ),
        [formik.values.conditions],
    );

    const lastConditionId = useMemo(() => {
        const last = formik.values.conditions.at(-1);
        return last?.id;
    }, [formik.values.conditions]);

    const defaultConditionIds = useMemo(
        () => new Set(row.defaultConditionIds),
        [row.defaultConditionIds],
    );
    const defaultRowConditions = useMemo(
        () =>
            formik.values.conditions.filter((condition) =>
                defaultConditionIds.has(condition.id),
            ),
        [defaultConditionIds, formik.values.conditions],
    );
    const defaultAmountIsCredit = useMemo(
        () =>
            defaultRowConditions.find(
                (condition) =>
                    condition.conditionType === FormRuleConditionType.AMOUNT,
            )?.amountIsCredit,
        [defaultRowConditions],
    );
    const editableConditionEntries = useMemo(
        () =>
            formik.values.conditions
                .map((condition, index) => ({ condition, index }))
                .filter(
                    ({ condition }) => !defaultConditionIds.has(condition.id),
                ),
        [defaultConditionIds, formik.values.conditions],
    );
    const hasRenderedCondition = formik.values.conditions.length > 0;
    const shouldRenderFallbackAddCondition = !hasRenderedCondition;
    const addConditionMenu = (
        <AddConditionMenu
            selectedConditionTypes={selectedConditionTypes}
            onAdd={handleAddCondition}
        />
    );
    const defaultConditionsAddConditionMenu =
        hasRenderedCondition && editableConditionEntries.length === 0
            ? addConditionMenu
            : undefined;

    const rowEntities = useMemo(() => {
        const entityIds = filters.entityIds ?? [];
        return entities.filter((entity) => entityIds.includes(entity.id));
    }, [entities, filters.entityIds]);
    const scopedEntityIds =
        filters.entityIds && filters.entityIds.length > 0
            ? filters.entityIds
            : undefined;

    const bucketCount = useActionRowBucketCount(row);
    const { deleteActionRow, isDeleting } = useDeleteActionRow(row);
    const shouldShowCreateRuleWarning = isCreateRuleMissingConditions(row);

    return (
        <FormikProvider value={formik}>
            <tr
                ref={setSortableRef}
                style={rowStyle}
                className={classNames(styles.actionRowTr, {
                    [styles.rowDragging]: isDragging,
                })}
            >
                {isRuleAuthoringAvailable && (
                    <td className={styles.conditionsCell}>
                        <div className={styles.conditionsFlow}>
                            <SharedConditions
                                conditions={defaultRowConditions}
                                filters={filters}
                                similarToFallbackLabel={similarToFallbackLabel}
                                appendToLastRow={
                                    defaultConditionsAddConditionMenu
                                }
                            />
                            {editableConditionEntries.map(
                                ({ condition, index }) => {
                                    const isLast =
                                        condition.id === lastConditionId;
                                    return (
                                        <div
                                            key={condition.id}
                                            className={styles.conditionRow}
                                        >
                                            <RuleConditionForm
                                                condition={condition}
                                                index={index}
                                                onRemove={handleRemoveCondition}
                                                selectedConditionTypes={
                                                    selectedConditionTypes
                                                }
                                                groupType={
                                                    RuleGroupType.TRANSACTION
                                                }
                                                removeTrigger="inline"
                                                labelStyle="plain"
                                                labelClassName={
                                                    styles.plainLabel
                                                }
                                                controlButtonSize={
                                                    DRAWER_CONTROL_BUTTON_SIZE
                                                }
                                                allowedAmountIsCredit={
                                                    defaultAmountIsCredit
                                                }
                                                amountDirectionTagClassName={
                                                    styles.sharedConditionValue
                                                }
                                            />
                                            {isLast && (
                                                <AddConditionMenu
                                                    selectedConditionTypes={
                                                        selectedConditionTypes
                                                    }
                                                    onAdd={handleAddCondition}
                                                />
                                            )}
                                        </div>
                                    );
                                },
                            )}
                            {shouldRenderFallbackAddCondition &&
                                addConditionMenu}
                        </div>
                    </td>
                )}

                <td className={styles.actionCell}>
                    <div className={styles.actionRow}>
                        <RuleActionForm
                            index={0}
                            onRemove={() => undefined}
                            labelStyle="plain"
                            labelClassName={styles.plainLabel}
                            controlButtonSize={DRAWER_CONTROL_BUTTON_SIZE}
                            scopedEntityIds={scopedEntityIds}
                        />
                    </div>
                </td>

                <td className={styles.entityCell}>
                    <EntityAvatarGroup entities={rowEntities} max={2} />
                </td>

                <td className={styles.transactionsCell}>
                    <div className={styles.transactionsCellInner}>
                        <span className={styles.countValue}>{bucketCount}</span>
                    </div>
                </td>

                {isRuleAuthoringAvailable && (
                    <td className={styles.createRuleCell}>
                        <div className={styles.createRuleCellInner}>
                            <div className={styles.createRuleToggleWrapper}>
                                <Switch
                                    checked={row.createRule}
                                    onCheckedChange={handleToggleCreateRule}
                                    label="Create rule"
                                    labelHidden
                                />
                                {shouldShowCreateRuleWarning && (
                                    <Tooltip
                                        content={
                                            CREATE_RULE_REQUIRES_CONDITION_TOOLTIP
                                        }
                                    >
                                        <button
                                            type="button"
                                            className={styles.createRuleAlert}
                                            aria-label={
                                                CREATE_RULE_REQUIRES_CONDITION_TOOLTIP
                                            }
                                        >
                                            <AlertIcon className="icon-color-yellow-500" />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </td>
                )}

                <td className={styles.rowActionsCell}>
                    {canReorderOrDelete && (
                        <div className={styles.rowActionsInner}>
                            <Button
                                variant="secondary"
                                size="md"
                                iconOnly
                                type="button"
                                {...sortableListeners}
                                {...sortableAttributes}
                                tabIndex={-1}
                                aria-label="Reorder action"
                                className={styles.rowActionButton}
                            >
                                <DragHandle6DotIcon />
                            </Button>
                            <Tooltip content="Delete action">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    iconOnly
                                    type="button"
                                    aria-label="Delete action"
                                    className={styles.rowActionButton}
                                    disabled={isDeleting}
                                    onClick={() => void deleteActionRow()}
                                >
                                    <CloseIcon />
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </td>
            </tr>
        </FormikProvider>
    );
};

interface SharedConditionsProps {
    conditions: TransactionRuleConditionFormSchema[];
    filters: TransactionFiltersDto;
    similarToFallbackLabel?: string;
    appendToLastRow?: React.ReactNode;
}

const SharedConditions: React.FC<SharedConditionsProps> = ({
    conditions,
    filters,
    similarToFallbackLabel,
    appendToLastRow,
}) => {
    const { data: normalizedDescriptionsData } = useNormalizedDescriptions();

    const sharedConditionRows = useMemo(() => {
        const descriptions =
            normalizedDescriptionsData?.normalizedDescriptions ?? [];

        return conditions
            .map((condition) => {
                if (
                    condition.conditionType === FormRuleConditionType.SIMILAR_TO
                ) {
                    const value = getSimilarToConditionDisplayValue({
                        condition,
                        descriptions,
                        mergedToCanonicalMap:
                            normalizedDescriptionsData?.mergedToCanonicalMap ??
                            {},
                        filters,
                        fallbackLabel: similarToFallbackLabel,
                    });

                    return value
                        ? {
                              id: condition.id,
                              label: "Similar to",
                              values: [value],
                          }
                        : undefined;
                }

                if (condition.conditionType === FormRuleConditionType.AMOUNT) {
                    return {
                        id: condition.id,
                        label: "Amount is",
                        values: [
                            condition.amountIsCredit ? "Cash in" : "Cash out",
                        ],
                    };
                }

                return undefined;
            })
            .filter((conditionRow) => !!conditionRow);
    }, [
        conditions,
        filters,
        normalizedDescriptionsData,
        similarToFallbackLabel,
    ]);

    if (sharedConditionRows.length === 0) {
        return null;
    }

    return (
        <div className={styles.sharedConditionsGrid}>
            {sharedConditionRows.map((condition, index) => (
                <React.Fragment key={condition.id}>
                    <span className={styles.plainLabel}>{condition.label}</span>
                    <span className={styles.sharedConditionValues}>
                        {condition.values.map((value) => (
                            <span
                                key={value}
                                className={styles.sharedConditionValue}
                                title={value}
                            >
                                {value}
                            </span>
                        ))}
                        {index === sharedConditionRows.length - 1 &&
                            appendToLastRow}
                    </span>
                </React.Fragment>
            ))}
        </div>
    );
};

interface NormalizedDescriptionDisplayRow {
    normalizedDescriptionId: string;
    normalizedDescription: string;
    sampleBankDescription: string | null;
}

function getNormalizedDescriptionLabel(
    description: NormalizedDescriptionDisplayRow,
): string {
    return (
        description.sampleBankDescription ?? description.normalizedDescription
    );
}

function getSimilarToConditionDisplayValue({
    condition,
    descriptions,
    mergedToCanonicalMap,
    filters,
    fallbackLabel,
}: {
    condition: TransactionRuleConditionFormSchema;
    descriptions: NormalizedDescriptionDisplayRow[];
    mergedToCanonicalMap: Record<string, string>;
    filters: TransactionFiltersDto;
    fallbackLabel?: string;
}): string | undefined {
    const conditionId = condition.normalizedDescriptionId;
    if (!conditionId) {
        return undefined;
    }

    const labelById = new Map(
        descriptions.map((description) => [
            description.normalizedDescriptionId,
            getNormalizedDescriptionLabel(description),
        ]),
    );

    const directLabel = labelById.get(conditionId);
    if (directLabel) {
        return directLabel;
    }

    const filterIds = [
        ...(filters.normalizedDescriptionIds ?? []),
        ...(filters.normalizedDescriptionId
            ? [filters.normalizedDescriptionId]
            : []),
    ];
    const matchingFilterId = filterIds.find(
        (id) => (mergedToCanonicalMap[id] ?? id) === conditionId,
    );
    if (matchingFilterId) {
        const filterLabel = labelById.get(matchingFilterId);
        if (filterLabel) {
            return filterLabel;
        }
    }

    const mergedChildId = Object.entries(mergedToCanonicalMap).find(
        ([childId, canonicalId]) =>
            canonicalId === conditionId && labelById.has(childId),
    )?.[0];
    if (mergedChildId) {
        return labelById.get(mergedChildId);
    }

    if (fallbackLabel) {
        return fallbackLabel;
    }

    return conditionId;
}

interface AddConditionMenuProps {
    selectedConditionTypes: FormRuleConditionType[];
    onAdd: (conditionType: FormRuleConditionType) => void;
}

const AddConditionMenu: React.FC<AddConditionMenuProps> = ({
    selectedConditionTypes,
    onAdd,
}) => {
    const { enableSimilarityRules } = useTypedFlags();
    const isClassesEnabled = useIsClassesEnabled();

    const options = useMemo(
        () =>
            getSupportedRuleConditionTypes({ isClassesEnabled })
                .filter((type) => {
                    if (type === FormRuleConditionType.ENTITY) {
                        return false;
                    }
                    if (
                        type === FormRuleConditionType.SIMILAR_TO &&
                        !enableSimilarityRules
                    ) {
                        return false;
                    }
                    if (selectedConditionTypes.includes(type)) {
                        return false;
                    }
                    return true;
                })
                .map((type) => ({
                    value: type,
                    label:
                        type === FormRuleConditionType.COUNTERPARTY
                            ? "Counterparty"
                            : type,
                })),
        [selectedConditionTypes, enableSimilarityRules, isClassesEnabled],
    );

    const disabled = options.length === 0;

    return (
        <Menu.Root
            trigger={
                <Button
                    variant="secondary"
                    size="md"
                    type="button"
                    disabled={disabled}
                    className={styles.addConditionButton}
                    leftIcon={<PlusIcon />}
                >
                    Condition
                </Button>
            }
        >
            {options.map((option) => (
                <Menu.Item
                    key={option.value}
                    onClick={() => onAdd(option.value)}
                >
                    {option.label}
                </Menu.Item>
            ))}
        </Menu.Root>
    );
};
